import React, { useState, useEffect } from 'react';
import { 
  Trophy, TrendingUp, BarChart3, Activity, 
  ChevronUp, ChevronDown, Award, Zap, Target,
  Calendar, Users, Crown, Star, X, Sword, TrendingDown, User
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, BarChart, Bar, ScatterChart, Scatter, Cell, PieChart, Pie
} from 'recharts';
import LeagueScoreDistribution from './LeagueScoreDistribution';
import LeaguePerformanceBanner from './LeaguePerformanceBanner';

// Helper function to get manager image paths - Consistent with other components
const getManagerImage = (managerName) => {
  if (!managerName) return null;
  const mainImage = `/${managerName.toLowerCase()}main.png`;
  const fallbackImage = `/${managerName.toLowerCase()}.png`;
  
  return {
    main: mainImage,
    fallback: fallbackImage
  };
};

// Helper function to get manager themed images (happy, angry, etc.)
const getManagerThemedImage = (managerName, theme) => {
  if (!managerName) return null;
  const mainImage = `/${managerName.toLowerCase()}${theme}.png`;
  const fallbackImage = `/${managerName.toLowerCase()}main.png`;
  
  return {
    main: mainImage,
    fallback: fallbackImage
  };
};

// Margin of Victory Distribution Component  
const LeagueMoVDistribution = ({ data, currentWeekMoV, currentWeekPercentile }) => {
  const [densityData, setDensityData] = useState([]);
  const [quantiles, setQuantiles] = useState({});

  // Simple smooth density estimation matching R's geom_density approach
  const smoothDensity = (values, bandwidth) => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const padding = range * 0.1; // Add padding like R does
    const start = min - padding;
    const end = max + padding;
    
    // Create many evaluation points for ultra-smooth curve
    const numPoints = 512; // High resolution like R uses
    const step = (end - start) / (numPoints - 1);
    const points = [];
    
    for (let i = 0; i < numPoints; i++) {
      const x = start + (i * step);
      let density = 0;
      
      // Simple Gaussian kernel density estimation
      for (const value of values) {
        const diff = (x - value) / bandwidth;
        density += Math.exp(-0.5 * diff * diff);
      }
      
      density = density / (values.length * bandwidth * Math.sqrt(2 * Math.PI));
      points.push({ x, y: density });
    }
    
    return points;
  };

  useEffect(() => {
    if (!data.length) return;

    const points = data.map(d => d.avgMoV).sort((a, b) => a - b);
    const min = Math.min(...points);
    const max = Math.max(...points);
    
    // Calculate quantiles
    const q0 = points[0];
    const q25 = points[Math.floor(points.length * 0.25)];
    const q50 = points[Math.floor(points.length * 0.50)];
    const q75 = points[Math.floor(points.length * 0.75)];
    const q100 = points[points.length - 1];
    
    setQuantiles({ q0, q25, q50, q75, q100 });
    
    // Calculate bandwidth using simplified Silverman's rule of thumb (like R default)
    const n = points.length;
    const stdDev = Math.sqrt(points.reduce((sum, x) => {
      const mean = points.reduce((a, b) => a + b, 0) / n;
      return sum + Math.pow(x - mean, 2);
    }, 0) / (n - 1));
    
    // R's default bandwidth calculation
    const iqr = q75 - q25;
    const bandwidth = 0.9 * Math.min(stdDev, iqr / 1.34) * Math.pow(n, -1/5);
    
    // Generate smooth density curve
    const smoothPoints = smoothDensity(points, bandwidth);
    
    // Assign quartiles to density points
    const processedDensity = smoothPoints.map(point => {
      let quartile = 1;
      if (point.x >= q25) quartile = 2;
      if (point.x >= q50) quartile = 3;
      if (point.x >= q75) quartile = 4;
      
      return { ...point, quartile };
    });
    
    setDensityData(processedDensity);
    
  }, [data]);

  // Local helper functions for MoV component
  const getMoVQuartileColor = (quartile) => {
    switch (quartile) {
      case 1: return '#ef4444'; // Red - close games
      case 2: return '#f97316'; // Orange  
      case 3: return '#eab308'; // Yellow
      case 4: return '#22c55e'; // Green - blowouts
      default: return '#6b7280';
    }
  };

  if (!densityData.length) {
    return <div className="h-72 sm:h-80 lg:h-[28rem] flex items-center justify-center text-gray-500">Loading distribution...</div>;
  }

  const movMinX = Math.min(...densityData.map(d => d.x));
  const movMaxX = Math.max(...densityData.map(d => d.x));
  const movMaxY = Math.max(...densityData.map(d => d.y));
  const isMobile = window.innerWidth < 640;
  const movChartWidth = isMobile ? 600 : 800;
  const movChartHeight = isMobile ? 240 : 320;

  // Create ultra-smooth SVG path using native SVG curves
  const createMoVSmoothPath = (points, fillToBottom = false) => {
    if (points.length < 2) return '';
    
    // Convert to screen coordinates
    const screenPoints = points.map(point => ({
      x: 20 + ((point.x - movMinX) / (movMaxX - movMinX)) * (movChartWidth - 40),
      y: movChartHeight - 20 - ((point.y / movMaxY) * (movChartHeight - 40))
    }));
    
    // Start path
    let path = `M ${screenPoints[0].x} ${screenPoints[0].y}`;
    
    // Use smooth cubic bezier curves for ultra-smooth lines
    for (let i = 1; i < screenPoints.length; i++) {
      const current = screenPoints[i];
      const previous = screenPoints[i - 1];
      
      // Calculate control points for smooth curve
      const cpx1 = previous.x + (current.x - previous.x) * 0.3;
      const cpy1 = previous.y;
      const cpx2 = current.x - (current.x - previous.x) * 0.3;
      const cpy2 = current.y;
      
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${current.x} ${current.y}`;
    }
    
    // Close path for filled areas
    if (fillToBottom) {
      const lastPoint = screenPoints[screenPoints.length - 1];
      const firstPoint = screenPoints[0];
      path += ` L ${lastPoint.x} ${movChartHeight - 20} L ${firstPoint.x} ${movChartHeight - 20} Z`;
    }
    
    return path;
  };

  return (
    <div className="h-72 sm:h-80 lg:h-[28rem] relative bg-white rounded-2xl p-3 sm:p-4 shadow-2xl border border-gray-100">
      <svg width="100%" height="100%" viewBox={`0 0 ${movChartWidth + 120} ${movChartHeight + 180}`} className="overflow-visible">
        {/* Define premium gradients and effects */}
        <defs>
          {/* Quartile-specific gradients */}
          <linearGradient id="movRedGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f87171" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="movOrangeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fb923c" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#ea580c" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="movYellowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#d97706" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="movGreenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0.1" />
          </linearGradient>
          
          {/* Premium shadow effects */}
          <filter id="movSoftShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.1" floodColor="#000"/>
          </filter>
          
          <filter id="movSubtleGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Chart area */}
        <g transform="translate(60, 40)">
          {/* Clean white background with subtle border */}
          <rect x="0" y="0" width={movChartWidth} height={movChartHeight} 
                fill="#ffffff" 
                stroke="#f1f5f9" 
                strokeWidth="1" 
                rx="12" 
                filter="url(#movSoftShadow)" />
          
          {/* Subtle horizontal grid lines */}
          {[0.25, 0.5, 0.75].map((level, i) => (
            <line
              key={i}
              x1="20"
              y1={movChartHeight - (level * movChartHeight)}
              x2={movChartWidth - 20}
              y2={movChartHeight - (level * movChartHeight)}
              stroke="#f8fafc"
              strokeWidth="1"
            />
          ))}
          
          {/* Quartile boundary lines */}
          {Object.values(quantiles).map((q, i) => {
            if (!q || q < movMinX || q > movMaxX) return null;
            const x = 20 + ((q - movMinX) / (movMaxX - movMinX)) * (movChartWidth - 40);
            const labels = ['0%', '25%', '50%', '75%', '100%'];
            
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1="20"
                  x2={x}
                  y2={movChartHeight - 20}
                  stroke="#e2e8f0"
                  strokeWidth="1"
                  strokeDasharray="3,6"
                />
                
                {/* Clean percentage labels */}
                <text
                  x={x}
                  y={movChartHeight + 30}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize={isMobile ? "11" : "13"}
                  fontWeight="600"
                >
                  {labels[i]}
                </text>
              </g>
            );
          })}
          
          {/* Draw quartile fills under the curve - each quartile gets its own color */}
          {[1, 2, 3, 4].map(quartile => {
            const quartilePoints = densityData.filter(d => d.quartile === quartile);
            if (quartilePoints.length === 0) return null;
            
            return (
              <path
                key={`mov-quartile-fill-${quartile}`}
                d={createMoVSmoothPath(quartilePoints, true)}
                fill={getMoVQuartileColor(quartile)}
                fillOpacity="0.6"
                stroke="none"
              />
            );
          })}
          
          {/* Main density curve outline */}
          <path
            d={createMoVSmoothPath(densityData, false)}
            fill="none"
            stroke="#1e293b"
            strokeWidth={isMobile ? "2" : "3"}
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#movSubtleGlow)"
          />
          
          {/* Current week simple dashed line indicator - more prominent purple */}
          {currentWeekMoV && currentWeekMoV >= movMinX && currentWeekMoV <= movMaxX && (
            <g>
              {(() => {
                const x = 20 + ((currentWeekMoV - movMinX) / (movMaxX - movMinX)) * (movChartWidth - 40);
                
                // Find the y-coordinate where the line intersects the curve
                const closestPoint = densityData.reduce((prev, curr) => 
                  Math.abs(curr.x - currentWeekMoV) < Math.abs(prev.x - currentWeekMoV) ? curr : prev
                );
                const intersectionY = movChartHeight - 20 - ((closestPoint.y / movMaxY) * (movChartHeight - 40));
                
                return (
                  <>
                    {/* Background glow for prominence */}
                    <line
                      x1={x}
                      y1="20"
                      x2={x}
                      y2={movChartHeight - 20}
                      stroke="#a855f7"
                      strokeWidth={isMobile ? "6" : "8"}
                      strokeLinecap="round"
                      opacity="0.3"
                    />
                    
                    {/* Main purple dashed line */}
                    <line
                      x1={x}
                      y1="20"
                      x2={x}
                      y2={movChartHeight - 20}
                      stroke="#8b5cf6"
                      strokeWidth={isMobile ? "3" : "4"}
                      strokeDasharray="12,6"
                      strokeLinecap="round"
                    />
                    
                    {/* Intersection point with curve */}
                    <circle
                      cx={x}
                      cy={intersectionY}
                      r={isMobile ? "4" : "5"}
                      fill="#8b5cf6"
                      stroke="#ffffff"
                      strokeWidth={isMobile ? "2" : "3"}
                    />
                  </>
                );
              })()}
            </g>
          )}
          
          {/* Clean X-axis values */}
          {(() => {
            const dataMin = Math.min(...data.map(d => d.avgMoV));
            const dataMax = Math.max(...data.map(d => d.avgMoV));
            const step = Math.ceil((dataMax - dataMin) / 4);
            const roundedMin = Math.ceil(dataMin / step) * step;
            const values = [];
            for (let v = roundedMin; v <= dataMax; v += step) {
              if (v >= dataMin && Math.abs(v - currentWeekMoV) > step * 0.3) values.push(v);
            }
            
            return values.map((value, i) => {
              const x = 20 + ((value - movMinX) / (movMaxX - movMinX)) * (movChartWidth - 40);
              return (
                <text
                  key={i}
                  x={x}
                  y={movChartHeight + 60}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize={isMobile ? "10" : "11"}
                  fontWeight="500"
                >
                  {Math.round(value * 10) / 10}
                </text>
              );
            });
          })()}
          
          {/* Clean X-axis label */}
          <text
            x={movChartWidth / 2}
            y={movChartHeight + 90}
            textAnchor="middle"
            fill="#374151"
            fontSize={isMobile ? "14" : "16"}
            fontWeight="600"
          >
            Average Margin of Victory
          </text>
        </g>
      </svg>
    </div>
  );
};

// Individual Manager Distribution Component
const ManagerScoreDistribution = ({ managerData, currentWeekStats }) => {
  const managers = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott'];
  const [selectedHeadshot, setSelectedHeadshot] = useState(null);
  
  const getManagerCurrentWeek = (manager) => {
    // Find the most recent week data for this manager
    const managerWeeks = managerData.filter(d => d.manager === manager);
    if (managerWeeks.length === 0) return { percentile: 0, totalPF: 0 };
    
    // Get the most recent week
    const mostRecentWeek = Math.max(...managerWeeks.map(d => d.leagueWeek));
    const currentWeekData = managerWeeks.find(d => d.leagueWeek === mostRecentWeek);
    
    return currentWeekData || { percentile: 0, totalPF: 0 };
  };

  const openHeadshotModal = (manager) => {
    setSelectedHeadshot(manager);
  };

  const closeHeadshotModal = () => {
    setSelectedHeadshot(null);
  };

  const generateManagerDensity = (managerName) => {
    const managerWeeks = managerData.filter(d => d.manager === managerName);
    if (managerWeeks.length === 0) return [];

    const points = managerWeeks.map(d => d.totalPF).sort((a, b) => a - b);
    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min;
    const padding = range * 0.1;
    const start = min - padding;
    const end = max + padding;
    
    const numPoints = 100;
    const step = (end - start) / (numPoints - 1);
    const densityPoints = [];
    
    // Calculate bandwidth
    const n = points.length;
    const stdDev = Math.sqrt(points.reduce((sum, x) => {
      const mean = points.reduce((a, b) => a + b, 0) / n;
      return sum + Math.pow(x - mean, 2);
    }, 0) / (n - 1));
    const bandwidth = 0.9 * stdDev * Math.pow(n, -1/5);
    
    for (let i = 0; i < numPoints; i++) {
      const x = start + (i * step);
      let density = 0;
      
      for (const value of points) {
        const diff = (x - value) / bandwidth;
        density += Math.exp(-0.5 * diff * diff);
      }
      
      density = density / (points.length * bandwidth * Math.sqrt(2 * Math.PI));
      densityPoints.push({ x, y: density });
    }
    
    return densityPoints;
  };

  const createManagerPath = (densityData, chartWidth, chartHeight) => {
    if (densityData.length === 0) return '';
    
    const minX = Math.min(...densityData.map(d => d.x));
    const maxX = Math.max(...densityData.map(d => d.x));
    const maxY = Math.max(...densityData.map(d => d.y));
    
    const screenPoints = densityData.map(point => ({
      x: 10 + ((point.x - minX) / (maxX - minX)) * (chartWidth - 20),
      y: chartHeight - 10 - ((point.y / maxY) * (chartHeight - 20))
    }));
    
    let path = `M ${screenPoints[0].x} ${screenPoints[0].y}`;
    
    for (let i = 1; i < screenPoints.length; i++) {
      const current = screenPoints[i];
      const previous = screenPoints[i - 1];
      
      const cpx1 = previous.x + (current.x - previous.x) * 0.3;
      const cpy1 = previous.y;
      const cpx2 = current.x - (current.x - previous.x) * 0.3;
      const cpy2 = current.y;
      
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${current.x} ${current.y}`;
    }
    
    return path;
  };

  const getOrdinal = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return `${num}st`;
    if (j === 2 && k !== 12) return `${num}nd`;
    if (j === 3 && k !== 13) return `${num}rd`;
    return `${num}th`;
  };

  const getScoreQuartileStyle = (percentile) => {
    if (percentile >= 75) {
      return {
        background: 'bg-gradient-to-r from-green-100 to-emerald-50',
        border: 'border-green-200',
        dot: 'bg-green-500',
        text: 'text-green-700'
      };
    } else if (percentile >= 50) {
      return {
        background: 'bg-gradient-to-r from-yellow-100 to-amber-50',
        border: 'border-yellow-200',
        dot: 'bg-yellow-500',
        text: 'text-yellow-700'
      };
    } else if (percentile >= 25) {
      return {
        background: 'bg-gradient-to-r from-orange-100 to-red-50',
        border: 'border-orange-200',
        dot: 'bg-orange-500',
        text: 'text-orange-700'
      };
    } else {
      return {
        background: 'bg-gradient-to-r from-red-100 to-pink-50',
        border: 'border-red-200',
        dot: 'bg-red-500',
        text: 'text-red-700'
      };
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Individual Manager Performance Distributions</h2>
        
        {/* Premium Performance Summary Card - Mobile optimized */}
        <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-red-50 rounded-2xl p-4 sm:p-6 border border-gray-200/50 shadow-lg mb-4 sm:mb-6">
          <div className="flex items-center mb-4 sm:mb-6">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-md">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <div className="ml-3">
              <h3 className="text-base sm:text-lg font-bold text-gray-900">Manager Performance Analysis</h3>
              <p className="text-xs sm:text-sm text-gray-600">Individual scoring relative to personal history</p>
            </div>
          </div>
          
          {/* Performance Cards Grid - Mobile responsive */}
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
            {/* Top Performer Card */}
            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl p-4 sm:p-6 text-white shadow-xl relative overflow-hidden">
              {/* Manager Happy Background Image - FIXED with toLowerCase and robust fallback */}
              <div 
                className="absolute inset-0 opacity-20 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${getManagerThemedImage(currentWeekStats.bestManager, 'happy')?.main})`,
                  backgroundSize: '80% auto',
                  backgroundPosition: 'center top',
                  filter: 'brightness(1.2) contrast(0.8)'
                }}
              ></div>
              
              <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full -translate-y-4 sm:-translate-y-8 translate-x-4 sm:translate-x-8"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center space-x-2">
                    <ChevronUp className="h-4 w-4 sm:h-6 sm:w-6 text-emerald-200" />
                    <span className="text-xs sm:text-sm font-semibold text-emerald-200 uppercase tracking-wide">
                      Outperformed Expectations
                    </span>
                  </div>
                  <div className="p-1.5 sm:p-2 bg-white/20 rounded-full">
                    <Trophy className="h-3 w-3 sm:h-5 sm:w-5 text-white" />
                  </div>
                </div>
                
                <div className="mb-2 sm:mb-3">
                  <span className="text-2xl sm:text-3xl font-black tracking-tight drop-shadow-md">
                    {currentWeekStats.bestManager}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="bg-white/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm">
                      <span className="text-xs sm:text-sm font-bold drop-shadow-sm">
                        {getOrdinal(Math.round(currentWeekStats.bestManagerPercentile))} percentile
                      </span>
                    </div>
                    <div className="text-emerald-200 text-xs sm:text-sm font-medium drop-shadow-sm">
                      Personal best territory
                    </div>
                  </div>
                  
                  {/* Points Scored */}
                  <div className="bg-white/15 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full backdrop-blur-sm inline-block">
                    <span className="text-base sm:text-lg font-bold drop-shadow-sm">
                      {(() => {
                        const bestManagerStats = currentWeekStats.managerStats?.find(m => m.manager === currentWeekStats.bestManager);
                        return bestManagerStats?.totalPF ? bestManagerStats.totalPF.toFixed(1) : 'N/A';
                      })()} points scored
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bottom Performer Card */}
            <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl p-4 sm:p-6 text-white shadow-xl relative overflow-hidden">
              {/* Manager Angry Background Image - FIXED with toLowerCase and robust fallback */}
              <div 
                className="absolute inset-0 opacity-20 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${getManagerThemedImage(currentWeekStats.worstManager, 'angry')?.main})`,
                  backgroundSize: '80% auto',
                  backgroundPosition: 'center top',
                  filter: 'brightness(1.2) contrast(0.8)'
                }}
              ></div>
              
              <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full -translate-y-4 sm:-translate-y-8 translate-x-4 sm:translate-x-8"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center space-x-2">
                    <ChevronDown className="h-4 w-4 sm:h-6 sm:w-6 text-red-200" />
                    <span className="text-xs sm:text-sm font-semibold text-red-200 uppercase tracking-wide">
                      Underperformed Expectations
                    </span>
                  </div>
                  <div className="p-1.5 sm:p-2 bg-white/20 rounded-full">
                    <TrendingDown className="h-3 w-3 sm:h-5 sm:w-5 text-white" />
                  </div>
                </div>
                
                <div className="mb-2 sm:mb-3">
                  <span className="text-2xl sm:text-3xl font-black tracking-tight drop-shadow-md">
                    {currentWeekStats.worstManager}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="bg-white/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm">
                      <span className="text-xs sm:text-sm font-bold drop-shadow-sm">
                        {getOrdinal(Math.round(currentWeekStats.worstManagerPercentile))} percentile
                      </span>
                    </div>
                    <div className="text-red-200 text-xs sm:text-sm font-medium drop-shadow-sm">
                      Below personal average
                    </div>
                  </div>
                  
                  {/* Points Scored */}
                  <div className="bg-white/15 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full backdrop-blur-sm inline-block">
                    <span className="text-base sm:text-lg font-bold drop-shadow-sm">
                      {(() => {
                        const worstManagerStats = currentWeekStats.managerStats?.find(m => m.manager === currentWeekStats.worstManager);
                        return worstManagerStats?.totalPF ? worstManagerStats.totalPF.toFixed(1) : 'N/A';
                      })()} points scored
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contextual Description */}
          <div className="mt-4 sm:mt-6 text-gray-700 text-xs sm:text-sm leading-relaxed bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-gray-200/50">
            <p className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-red-500 rounded-full flex-shrink-0"></span>
              <span>
                Each manager's performance is measured against their own historical scoring distribution, 
                showing who exceeded or fell short of their personal expectations this week.
              </span>
            </p>
          </div>
        </div>
      </div>
      
      {/* Manager Grid - Mobile responsive */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {managers
          .map(manager => {
            const currentWeek = getManagerCurrentWeek(manager);
            return { manager, percentile: currentWeek.percentile || 0 };
          })
          .sort((a, b) => b.percentile - a.percentile) // Sort by percentile descending (best to worst)
          .map(({ manager }, index) => {
            const densityData = generateManagerDensity(manager);
            const currentWeek = getManagerCurrentWeek(manager);
            const isMobile = window.innerWidth < 640;
            const chartWidth = isMobile ? 160 : 200;
            const chartHeight = isMobile ? 100 : 120;
            const quartileStyle = getScoreQuartileStyle(currentWeek.percentile || 0);
            const images = getManagerImage(manager);
            
            if (densityData.length === 0) return null;
            
            const minX = Math.min(...densityData.map(d => d.x));
            const maxX = Math.max(...densityData.map(d => d.x));
            
            return (
              <div key={manager} className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                <div className="text-center mb-3 sm:mb-4">
                  {/* Manager Headshot - Now Clickable with FIXED image handling */}
                  <div className="mb-2 sm:mb-3 flex justify-center">
                    <div 
                      className="w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-lg border-3 border-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-200"
                      onClick={() => openHeadshotModal(manager)}
                    >
                      <img 
                        src={images?.main}
                        alt={`${manager} headshot`}
                        className="w-full h-full object-cover hover:brightness-110 transition-all duration-200"
                        onError={(e) => {
                          if (images?.fallback && e.target.src !== images.fallback) {
                            e.target.src = images.fallback;
                          } else {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div 
                        className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg hidden hover:from-gray-300 hover:to-gray-500 transition-all duration-200"
                        style={{ display: 'none' }}
                      >
                        {manager.charAt(0)}
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{manager}</h3>
                  <div className="space-y-1">
                    <div className="text-xs sm:text-sm text-purple-600 font-semibold tracking-wide">
                      {currentWeek.percentile ? getOrdinal(Math.round(currentWeek.percentile)) : '0th'} percentile
                    </div>
                    <div className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full shadow-sm ${quartileStyle.background} ${quartileStyle.border}`}>
                      <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full mr-1 sm:mr-2 ${quartileStyle.dot}`}></div>
                      <span className={`text-xs font-bold tracking-wider ${quartileStyle.text}`}>
                        {currentWeek.totalPF ? currentWeek.totalPF.toFixed(1) : '0.0'} PTS
                      </span>
                    </div>
                  </div>
                </div>
              
              <svg width="100%" height={isMobile ? "120" : "140"} viewBox={`0 0 ${chartWidth} ${isMobile ? 120 : 140}`} className="overflow-visible">
                <defs>
                  <filter id={`managerGlow-${manager}-${index}`} x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Grid lines */}
                {[0.25, 0.5, 0.75].map((level, i) => (
                  <line
                    key={i}
                    x1="10"
                    y1={chartHeight - (level * chartHeight)}
                    x2={chartWidth - 10}
                    y2={chartHeight - (level * chartHeight)}
                    stroke="#e5e7eb"
                    strokeWidth="0.5"
                  />
                ))}
                
                {/* Density curve */}
                <path
                  d={createManagerPath(densityData, chartWidth, chartHeight)}
                  fill="none"
                  stroke="#374151"
                  strokeWidth={isMobile ? "1.5" : "2"}
                  strokeLinecap="round"
                  filter={`url(#managerGlow-${manager}-${index})`}
                />
                
                {/* Current week indicator - purple theme */}
                <g>
                  {(() => {
                    // Calculate position based on current week's total points
                    const currentPoints = currentWeek.totalPF || 0;
                    
                    // Ensure we have valid data
                    if (!currentPoints || currentPoints < minX || currentPoints > maxX) {
                      console.log(`${manager}: currentPoints=${currentPoints}, minX=${minX}, maxX=${maxX}`);
                      return null;
                    }
                    
                    const x = 10 + ((currentPoints - minX) / (maxX - minX)) * (chartWidth - 20);
                    
                    return (
                      <>
                        {/* Background line for visibility */}
                        <line
                          x1={x}
                          y1="5"
                          x2={x}
                          y2={chartHeight - 5}
                          stroke="#c4b5fd"
                          strokeWidth={isMobile ? "3" : "4"}
                          strokeLinecap="round"
                          opacity="0.5"
                        />
                        
                        {/* Main indicator line */}
                        <line
                          x1={x}
                          y1="5"
                          x2={x}
                          y2={chartHeight - 5}
                          stroke="#8b5cf6"
                          strokeWidth={isMobile ? "1.5" : "2"}
                          strokeDasharray="4,2"
                          strokeLinecap="round"
                        />
                        
                        {/* Bottom indicator circle */}
                        <circle
                          cx={x}
                          cy={chartHeight - 5}
                          r={isMobile ? "2" : "3"}
                          fill="#8b5cf6"
                          stroke="#ffffff"
                          strokeWidth="1"
                        />
                        
                        {/* Top indicator with percentile */}
                        <rect
                          x={x - (isMobile ? 10 : 12)}
                          y="-5"
                          width={isMobile ? "20" : "24"}
                          height={isMobile ? "10" : "12"}
                          fill="#8b5cf6"
                          rx="6"
                        />
                        <text
                          x={x}
                          y="3"
                          textAnchor="middle"
                          fill="white"
                          fontSize={isMobile ? "7" : "8"}
                          fontWeight="bold"
                        >
                          {getOrdinal(Math.round(currentWeek.percentile || 0))}
                        </text>
                      </>
                    );
                  })()}
                </g>
                
                {/* X-axis - remove numerical values */}
                <line
                  x1="10"
                  y1={chartHeight}
                  x2={chartWidth - 10}
                  y2={chartHeight}
                  stroke="#9ca3af"
                  strokeWidth="1"
                />
              </svg>
            </div>
          );
                  })}
      </div>

      {/* Headshot Modal - Mobile optimized with FIXED image handling */}
      {selectedHeadshot && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl max-w-sm sm:max-w-lg w-full border border-white/30 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 sm:p-6 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20"></div>
              <div className="relative flex items-center justify-between">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                    {selectedHeadshot}
                  </h2>
                  <p className="text-blue-100 text-sm font-medium">
                    Manager Profile
                  </p>
                </div>
                <button
                  onClick={closeHeadshotModal}
                  className="p-2 hover:bg-white/20 rounded-xl sm:rounded-2xl transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-8 text-center bg-gradient-to-br from-gray-50/50 to-white/50">
              <div className="mb-4 sm:mb-6">
                <div className="w-32 h-32 sm:w-48 sm:h-48 mx-auto rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                  <img 
                    src={getManagerImage(selectedHeadshot)?.main}
                    alt={`${selectedHeadshot} headshot`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const images = getManagerImage(selectedHeadshot);
                      if (images?.fallback && e.target.src !== images.fallback) {
                        e.target.src = images.fallback;
                      } else {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                  <div 
                    className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white font-bold text-4xl sm:text-6xl hidden"
                    style={{ display: 'none' }}
                  >
                    {selectedHeadshot.charAt(0)}
                  </div>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {selectedHeadshot}
                </h3>
                
                {/* Get current week stats for this manager */}
                {(() => {
                  const currentWeek = getManagerCurrentWeek(selectedHeadshot);
                  const quartileStyle = getScoreQuartileStyle(currentWeek.percentile || 0);
                  
                  return (
                    <div className="space-y-2 sm:space-y-3">
                      <div className="text-base sm:text-lg text-purple-600 font-bold tracking-wide">
                        Current Performance: {currentWeek.percentile ? getOrdinal(Math.round(currentWeek.percentile)) : '0th'} Percentile
                      </div>
                      
                      <div className={`inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl shadow-lg ${quartileStyle.background} ${quartileStyle.border}`}>
                        <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 ${quartileStyle.dot}`}></div>
                        <span className={`text-base sm:text-lg font-bold tracking-wider ${quartileStyle.text}`}>
                          {currentWeek.totalPF ? currentWeek.totalPF.toFixed(1) : '0.0'} Points This Week
                        </span>
                      </div>
                      
                      <div className="text-gray-600 text-xs sm:text-sm px-2">
                        Performance relative to their personal historical distribution
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LeaguePerformance = ({ h2hData, finalData, loading, error }) => {
  const [leagueWeekData, setLeagueWeekData] = useState([]);
  const [managerWeekData, setManagerWeekData] = useState([]);
  const [leagueMoVData, setLeagueMoVData] = useState([]);
  const [topScoringWeeks, setTopScoringWeeks] = useState([]);
  const [bottomScoringWeeks, setBottomScoringWeeks] = useState([]);
  const [topBlowouts, setTopBlowouts] = useState([]);
  const [topNailbiters, setTopNailbiters] = useState([]);
  const [currentWeekStats, setCurrentWeekStats] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weekMatchups, setWeekMatchups] = useState([]);

  useEffect(() => {
    if (h2hData.length === 0) return;

    // Calculate League Week Points Data
    const leagueWeekStatsMap = new Map();
    
    h2hData
      .filter(game => game.Type === 'Regular Season')
      .forEach(game => {
        const leagueWeek = game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week);
        const current = leagueWeekStatsMap.get(leagueWeek) || { leagueWeek, totalPF: 0, year: game.Year, week: game.Week };
        current.totalPF += game.PF || 0;
        leagueWeekStatsMap.set(leagueWeek, current);
      });

    const leagueWeekStats = Array.from(leagueWeekStatsMap.values())
      .sort((a, b) => a.leagueWeek - b.leagueWeek);

    // Add percentiles
    const sortedByPF = [...leagueWeekStats].sort((a, b) => a.totalPF - b.totalPF);
    leagueWeekStats.forEach(week => {
      const rank = sortedByPF.findIndex(w => w.leagueWeek === week.leagueWeek);
      week.percentile = (rank / (sortedByPF.length - 1)) * 100;
    });

    setLeagueWeekData(leagueWeekStats);

    // Calculate Manager Week Data
    const managerWeekStatsMap = new Map();
    const managers = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott'];
    
    h2hData
      .filter(game => game.Type === 'Regular Season' && managers.includes(game.Manager1))
      .forEach(game => {
        const leagueWeek = game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week);
        const key = `${game.Manager1}-${leagueWeek}`;
        const current = managerWeekStatsMap.get(key) || { 
          manager: game.Manager1, 
          leagueWeek, 
          totalPF: 0, 
          year: game.Year, 
          week: game.Week 
        };
        current.totalPF += game.PF || 0;
        managerWeekStatsMap.set(key, current);
      });

    const managerWeekStats = Array.from(managerWeekStatsMap.values());
    
    // Add percentiles for each manager
    managers.forEach(manager => {
      const managerData = managerWeekStats.filter(d => d.manager === manager);
      const sortedManagerData = [...managerData].sort((a, b) => a.totalPF - b.totalPF);
      
      managerData.forEach(week => {
        const rank = sortedManagerData.findIndex(w => w.leagueWeek === week.leagueWeek);
        week.percentile = sortedManagerData.length > 1 ? (rank / (sortedManagerData.length - 1)) * 100 : 50;
      });
    });

    setManagerWeekData(managerWeekStats);

    // Calculate League MoV Data
    const leagueMoVStatsMap = new Map();
    
    h2hData
      .filter(game => game.Outcome === 'Win' && game.Type === 'Regular Season')
      .forEach(game => {
        const leagueWeek = game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week);
        const current = leagueMoVStatsMap.get(leagueWeek) || { 
          leagueWeek, 
          totalMoV: 0, 
          gameCount: 0,
          year: game.Year, 
          week: game.Week 
        };
        current.totalMoV += game.MoV || 0;
        current.gameCount += 1;
        leagueMoVStatsMap.set(leagueWeek, current);
      });

    const leagueMoVStats = Array.from(leagueMoVStatsMap.values())
      .map(week => ({
        ...week,
        avgMoV: week.gameCount > 0 ? week.totalMoV / week.gameCount : 0
      }))
      .sort((a, b) => a.leagueWeek - b.leagueWeek);

    // Add percentiles for MoV
    const sortedByMoV = [...leagueMoVStats].sort((a, b) => a.avgMoV - b.avgMoV);
    leagueMoVStats.forEach(week => {
      const rank = sortedByMoV.findIndex(w => w.leagueWeek === week.leagueWeek);
      week.percentile = (rank / (sortedByMoV.length - 1)) * 100;
    });

    setLeagueMoVData(leagueMoVStats);

    // Calculate Top/Bottom Scoring Weeks
    const topWeeks = [...leagueWeekStats]
      .sort((a, b) => b.totalPF - a.totalPF)
      .slice(0, 10)
      .map((week, index) => ({
        rank: index + 1,
        week: `${week.year} Week ${week.week}`,
        points: week.totalPF.toFixed(1)
      }));

    const bottomWeeks = [...leagueWeekStats]
      .sort((a, b) => a.totalPF - b.totalPF)
      .slice(0, 10)
      .map((week, index) => ({
        rank: index + 1,
        week: `${week.year} Week ${week.week}`,
        points: week.totalPF.toFixed(1)
      }));

    setTopScoringWeeks(topWeeks);
    setBottomScoringWeeks(bottomWeeks);

    // Calculate Top Blowouts and Nail-biters
    const blowouts = [...leagueMoVStats]
      .sort((a, b) => b.avgMoV - a.avgMoV)
      .slice(0, 10)
      .map((week, index) => ({
        rank: index + 1,
        week: `${week.year} Week ${week.week}`,
        avgMoV: week.avgMoV.toFixed(2)
      }));

    const nailbiters = [...leagueMoVStats]
      .sort((a, b) => a.avgMoV - b.avgMoV)
      .slice(0, 10)
      .map((week, index) => ({
        rank: index + 1,
        week: `${week.year} Week ${week.week}`,
        avgMoV: week.avgMoV.toFixed(2)
      }));

    setTopBlowouts(blowouts);
    setTopNailbiters(nailbiters);

    // Calculate current week stats
    if (leagueWeekStats.length > 0 && leagueMoVStats.length > 0 && managerWeekStats.length > 0) {
      const lastLeagueWeek = Math.max(...leagueWeekStats.map(w => w.leagueWeek));
      const lastWeekLeague = leagueWeekStats.find(w => w.leagueWeek === lastLeagueWeek);
      const lastWeekMoV = leagueMoVStats.find(w => w.leagueWeek === lastLeagueWeek);
      
      // Find best and worst performing managers last week
      const lastWeekManagers = managerWeekStats
        .filter(d => d.leagueWeek === lastLeagueWeek)
        .sort((a, b) => b.percentile - a.percentile);

      setCurrentWeekStats({
        leagueWeek: lastLeagueWeek,
        weekNumber: lastWeekLeague?.week || 0,
        seasonYear: lastWeekLeague?.year || 0,
        totalPoints: lastWeekLeague?.totalPF || 0,
        pointsPercentile: lastWeekLeague?.percentile || 0,
        avgMoV: lastWeekMoV?.avgMoV || 0,
        movPercentile: lastWeekMoV?.percentile || 0,
        bestManager: lastWeekManagers[0]?.manager || '',
        bestManagerPercentile: lastWeekManagers[0]?.percentile || 0,
        worstManager: lastWeekManagers[lastWeekManagers.length - 1]?.manager || '',
        worstManagerPercentile: lastWeekManagers[lastWeekManagers.length - 1]?.percentile || 0,
        managerStats: lastWeekManagers
      });
    }

  }, [h2hData]);

  const getOrdinal = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return `${num}st`;
    if (j === 2 && k !== 12) return `${num}nd`;
    if (j === 3 && k !== 13) return `${num}rd`;
    return `${num}th`;
  };

  const getPercentileColor = (percentile) => {
    if (percentile >= 75) return 'text-green-600';
    if (percentile >= 50) return 'text-yellow-600';
    if (percentile >= 25) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleWeekClick = (year, week, category = null, rank = null) => {
    // Find all games for this specific week
    const weekGames = h2hData.filter(game => 
      game.Year === year && 
      game.Week === week && 
      game.Type === 'Regular Season'
    );
    
    // Group games into matchups (each matchup appears twice in data)
    const matchupMap = new Map();
    weekGames.forEach(game => {
      const key = [game.Manager1, game.Manager2].sort().join('-');
      if (!matchupMap.has(key)) {
        const opposingGame = weekGames.find(g => 
          g.Manager1 === game.Manager2 && g.Manager2 === game.Manager1
        );
        
        matchupMap.set(key, {
          manager1: game.Manager1,
          manager1Score: game.PF,
          manager2: game.Manager2,
          manager2Score: opposingGame ? opposingGame.PF : 0,
          winner: game.Outcome === 'Win' ? game.Manager1 : game.Manager2,
          margin: Math.abs(game.PF - (opposingGame ? opposingGame.PF : 0))
        });
      }
    });
    
    let matchups = Array.from(matchupMap.values());
    
    // Sort matchups based on the category
    if (category) {
      switch (category) {
        case 'High Scoring':
          matchups.sort((a, b) => (b.manager1Score + b.manager2Score) - (a.manager1Score + a.manager2Score));
          break;
        case 'Low Scoring':
          matchups.sort((a, b) => (a.manager1Score + a.manager2Score) - (b.manager1Score + b.manager2Score));
          break;
        case 'Blowouts':
          matchups.sort((a, b) => b.margin - a.margin);
          break;
        case 'Nail-biters':
          matchups.sort((a, b) => a.margin - b.margin);
          break;
        default:
          // Default sort by total points
          matchups.sort((a, b) => (b.manager1Score + b.manager2Score) - (a.manager1Score + a.manager2Score));
      }
    }
    
    setWeekMatchups(matchups);
    setSelectedWeek({ year, week, category, rank });
  };

  const closeModal = () => {
    setSelectedWeek(null);
    setWeekMatchups([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading league performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Trophy className="h-8 w-8 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Use the new LeaguePerformanceBanner component */}
      <LeaguePerformanceBanner 
        currentWeekStats={currentWeekStats}
        getOrdinal={getOrdinal}
      />

      {/* Current Week Stats Cards - Mobile responsive */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Last Week Points</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {currentWeekStats.totalPoints?.toFixed(1) || '0'}
              </p>
              <p className={`text-xs sm:text-sm font-medium ${getPercentileColor(currentWeekStats.pointsPercentile)}`}>
                {getOrdinal(Math.round(currentWeekStats.pointsPercentile))} percentile
              </p>
            </div>
            <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Avg Margin of Victory</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {currentWeekStats.avgMoV?.toFixed(2) || '0'}
              </p>
              <p className={`text-xs sm:text-sm font-medium ${getPercentileColor(currentWeekStats.movPercentile)}`}>
                {getOrdinal(Math.round(currentWeekStats.movPercentile))} percentile
              </p>
            </div>
            <Target className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Top Performer</p>
              <p className="text-base sm:text-xl font-bold text-green-600">
                {currentWeekStats.bestManager || 'N/A'}
              </p>
              <p className="text-xs sm:text-sm font-medium text-gray-500">
                {getOrdinal(Math.round(currentWeekStats.bestManagerPercentile))} percentile
              </p>
            </div>
            <ChevronUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Bottom Performer</p>
              <p className="text-base sm:text-xl font-bold text-red-600">
                {currentWeekStats.worstManager || 'N/A'}
              </p>
              <p className="text-xs sm:text-sm font-medium text-gray-500">
                {getOrdinal(Math.round(currentWeekStats.worstManagerPercentile))} percentile
              </p>
            </div>
            <ChevronDown className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* League-Wide Scoring Distribution - Mobile optimized */}
      <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Beasts of Bellevue League Scoring</h2>
          
          {/* Premium Week Information Card - Mobile responsive */}
          <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 rounded-2xl p-4 sm:p-6 border border-slate-200/50 shadow-lg mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">Latest Week Analysis</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Most recent completed regular season</p>
                </div>
              </div>
              
              {/* Week Badge - Mobile responsive */}
              <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-md">
                  <span className="text-white font-bold text-xs sm:text-sm tracking-wide">
                    {currentWeekStats.seasonYear} Week {currentWeekStats.weekNumber}
                  </span>
                </div>
                <div className="bg-gradient-to-r from-slate-600 to-gray-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full shadow-md">
                  <span className="text-white font-medium text-xs tracking-wide">
                    League Week {currentWeekStats.leagueWeek}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Points and Percentile Display - Mobile responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="text-center sm:text-left">
                <div className="mb-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Total League Points</span>
                </div>
                <div className="flex items-baseline justify-center sm:justify-start space-x-2">
                  <span className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {currentWeekStats.totalPoints?.toFixed(1)}
                  </span>
                  <span className="text-base sm:text-lg font-medium text-gray-500">points</span>
                </div>
              </div>
              
              <div className="text-center sm:text-right">
                <div className="mb-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Historical Ranking</span>
                </div>
                <div className="flex items-center justify-center sm:justify-end space-x-2">
                  <div className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-md ${
                    currentWeekStats.pointsPercentile >= 75 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                      : currentWeekStats.pointsPercentile >= 50
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                      : currentWeekStats.pointsPercentile >= 25
                      ? 'bg-gradient-to-r from-orange-500 to-red-500'
                      : 'bg-gradient-to-r from-red-500 to-pink-600'
                  }`}>
                    <span className="text-white font-bold text-sm sm:text-lg tracking-wide">
                      {getOrdinal(Math.round(currentWeekStats.pointsPercentile))} percentile
                    </span>
                  </div>
                  <div className="p-1.5 sm:p-2 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full shadow-sm">
                    <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contextual Description */}
          <div className="text-gray-700 text-sm sm:text-base leading-relaxed bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-gray-200/50">
            <p className="flex items-start space-x-2">
              <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex-shrink-0 mt-2"></span>
              <span>
                This week's league-wide scoring performance shows how the combined effort of all managers compares to
                <span className="font-semibold text-gray-800"> {leagueWeekData.length} total weeks</span> in league history.
              </span>
            </p>
          </div>
        </div>
        <LeagueScoreDistribution 
          data={leagueWeekData} 
          currentWeekPoints={currentWeekStats.totalPoints}
          currentWeekPercentile={currentWeekStats.pointsPercentile}
          currentWeekInfo={{
            leagueWeek: currentWeekStats.leagueWeek,
            seasonWeek: currentWeekStats.weekNumber,
            seasonYear: currentWeekStats.seasonYear
          }}
        />
      </div>

      {/* Individual Manager Performance Distributions */}
      <ManagerScoreDistribution 
        managerData={managerWeekData}
        currentWeekStats={currentWeekStats}
      />

      {/* League Margin of Victory Distribution - Mobile optimized */}
      <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Beasts of Bellevue League Margin of Victory</h2>
          
          {/* Premium Margin of Victory Information Card - Mobile responsive */}
          <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 rounded-2xl p-4 sm:p-6 border border-slate-200/50 shadow-lg mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md">
                  <Target className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">Competitive Analysis</h3>
                  <p className="text-xs sm:text-sm text-gray-600">How close were the matchups this week?</p>
                </div>
              </div>
              
              {/* Week Badge - Mobile responsive */}
              <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-md">
                  <span className="text-white font-bold text-xs sm:text-sm tracking-wide">
                    {currentWeekStats.seasonYear} Week {currentWeekStats.weekNumber}
                  </span>
                </div>
                <div className="bg-gradient-to-r from-slate-600 to-gray-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full shadow-md">
                  <span className="text-white font-medium text-xs tracking-wide">
                    League Week {currentWeekStats.leagueWeek}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Margin and Percentile Display - Mobile responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="text-center sm:text-left">
                <div className="mb-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Average Margin of Victory</span>
                </div>
                <div className="flex items-baseline justify-center sm:justify-start space-x-2">
                  <span className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {currentWeekStats.avgMoV?.toFixed(2)}
                  </span>
                  <span className="text-base sm:text-lg font-medium text-gray-500">points</span>
                </div>
              </div>
              
              <div className="text-center sm:text-right">
                <div className="mb-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Competitiveness Ranking</span>
                </div>
                <div className="flex items-center justify-center sm:justify-end space-x-2">
                  <div className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-md ${
                    currentWeekStats.movPercentile >= 75 
                      ? 'bg-gradient-to-r from-red-500 to-pink-600' 
                      : currentWeekStats.movPercentile >= 50
                      ? 'bg-gradient-to-r from-orange-500 to-red-500'
                      : currentWeekStats.movPercentile >= 25
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600'
                  }`}>
                    <span className="text-white font-bold text-sm sm:text-lg tracking-wide">
                      {getOrdinal(Math.round(currentWeekStats.movPercentile))} percentile
                    </span>
                  </div>
                  <div className="p-1.5 sm:p-2 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full shadow-sm">
                    <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contextual Description */}
          <div className="text-gray-700 text-sm sm:text-base leading-relaxed bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-gray-200/50">
            <p className="flex items-start space-x-2">
              <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex-shrink-0 mt-2"></span>
              <span>
                This week's competitive landscape shows how the average margin of victory compares to
                <span className="font-semibold text-gray-800"> {leagueMoVData.length} total weeks</span> in league history.
              </span>
            </p>
          </div>
        </div>
        <LeagueMoVDistribution 
          data={leagueMoVData} 
          currentWeekMoV={currentWeekStats.avgMoV}
          currentWeekPercentile={currentWeekStats.movPercentile}
        />
      </div>

      {/* Record Tables Grid - Mobile optimized glassmorphism */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
        {/* Top 10 High Scoring Weeks */}
        <div className="relative">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 rounded-2xl sm:rounded-3xl blur-sm opacity-20"></div>
          <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 border border-white/30">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg sm:rounded-xl shadow-lg">
                <Trophy className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-gray-900 ml-2 sm:ml-3">Top High Scoring Weeks</h3>
            </div>
            <div className="space-y-2 sm:space-y-3">
              {topScoringWeeks.map((week, index) => (
                <div 
                  key={index}
                  onClick={() => handleWeekClick(parseInt(week.week.split(' ')[0]), parseInt(week.week.split(' ')[2]), 'High Scoring', week.rank)}
                  className={`relative overflow-hidden rounded-lg sm:rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer ${
                    index === 0 
                      ? 'bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 border-2 border-gradient-to-r from-yellow-200 to-orange-300 shadow-md' 
                      : 'bg-white/60 backdrop-blur-sm border border-gray-200/50 hover:bg-white/80'
                  }`}
                >
                  {index === 0 && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500"></div>
                  )}
                  <div className="flex justify-between items-center p-3 sm:p-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                        index === 0 
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-lg' 
                          : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 border border-gray-200'
                      }`}>
                        {week.rank}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-xs sm:text-sm">{week.week}</div>
                        <div className={`text-xs font-medium ${
                          index === 0 ? 'text-orange-600' : 'text-gray-500'
                        }`}>
                          {index === 0 ? 'League Record' : ''}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm sm:text-lg font-bold transition-colors duration-200 ${
                      index === 0 ? 'text-orange-600' : 'text-gray-700'
                    }`}>
                      {week.points}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom 10 Scoring Weeks */}
        <div className="relative">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-400 via-pink-400 to-purple-500 rounded-2xl sm:rounded-3xl blur-sm opacity-20"></div>
          <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 border border-white/30">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-red-400 to-pink-500 rounded-lg sm:rounded-xl shadow-lg">
                <ChevronDown className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-gray-900 ml-2 sm:ml-3">Lowest Scoring Weeks</h3>
            </div>
            <div className="space-y-2 sm:space-y-3">
              {bottomScoringWeeks.map((week, index) => (
                <div 
                  key={index}
                  onClick={() => handleWeekClick(parseInt(week.week.split(' ')[0]), parseInt(week.week.split(' ')[2]), 'Low Scoring', week.rank)}
                  className={`relative overflow-hidden rounded-lg sm:rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer ${
                    index === 0 
                      ? 'bg-gradient-to-r from-red-50 via-pink-50 to-purple-50 border-2 border-gradient-to-r from-red-200 to-pink-300 shadow-md' 
                      : 'bg-white/60 backdrop-blur-sm border border-gray-200/50 hover:bg-white/80'
                  }`}
                >
                  {index === 0 && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-400 via-pink-400 to-purple-500"></div>
                  )}
                  <div className="flex justify-between items-center p-3 sm:p-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                        index === 0 
                          ? 'bg-gradient-to-r from-red-400 to-pink-500 text-white shadow-lg' 
                          : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 border border-gray-200'
                      }`}>
                        {week.rank}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-xs sm:text-sm">{week.week}</div>
                        <div className={`text-xs font-medium ${
                          index === 0 ? 'text-pink-600' : 'text-gray-500'
                        }`}>
                          {index === 0 ? 'League Record' : ''}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm sm:text-lg font-bold transition-colors duration-200 ${
                      index === 0 ? 'text-pink-600' : 'text-gray-700'
                    }`}>
                      {week.points}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top 10 Blowouts */}
        <div className="relative">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-amber-400 to-yellow-500 rounded-2xl sm:rounded-3xl blur-sm opacity-20"></div>
          <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 border border-white/30">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg sm:rounded-xl shadow-lg">
                <Zap className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-gray-900 ml-2 sm:ml-3">Biggest Blowouts</h3>
            </div>
            <div className="space-y-2 sm:space-y-3">
              {topBlowouts.map((week, index) => (
                <div 
                  key={index}
                  onClick={() => handleWeekClick(parseInt(week.week.split(' ')[0]), parseInt(week.week.split(' ')[2]), 'Blowouts', week.rank)}
                  className={`relative overflow-hidden rounded-lg sm:rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer ${
                    index === 0 
                      ? 'bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 border-2 border-gradient-to-r from-orange-200 to-amber-300 shadow-md' 
                      : 'bg-white/60 backdrop-blur-sm border border-gray-200/50 hover:bg-white/80'
                  }`}
                >
                  {index === 0 && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-500"></div>
                  )}
                  <div className="flex justify-between items-center p-3 sm:p-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 
                          ? 'bg-gradient-to-r from-orange-400 to-amber-500 text-white shadow-lg' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {week.rank}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-xs sm:text-sm">{week.week}</div>
                        <div className={`text-xs font-medium ${
                          index === 0 ? 'text-amber-600' : 'text-gray-500'
                        }`}>
                          {index === 0 ? 'League Record' : ''}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm sm:text-xl font-bold ${
                      index === 0 ? 'text-amber-600' : 'text-orange-600'
                    }`}>
                      {week.avgMoV}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top 10 Nail-biters */}
        <div className="relative">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-indigo-400 to-blue-500 rounded-2xl sm:rounded-3xl blur-sm opacity-20"></div>
          <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 border border-white/30">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-lg sm:rounded-xl shadow-lg">
                <Users className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-gray-900 ml-2 sm:ml-3">Closest Nail-biters</h3>
            </div>
            <div className="space-y-2 sm:space-y-3">
              {topNailbiters.map((week, index) => (
                <div 
                  key={index}
                  onClick={() => handleWeekClick(parseInt(week.week.split(' ')[0]), parseInt(week.week.split(' ')[2]), 'Nail-biters', week.rank)}
                  className={`relative overflow-hidden rounded-lg sm:rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer ${
                    index === 0 
                      ? 'bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border-2 border-gradient-to-r from-purple-200 to-indigo-300 shadow-md' 
                      : 'bg-white/60 backdrop-blur-sm border border-gray-200/50 hover:bg-white/80'
                  }`}
                >
                  {index === 0 && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-500"></div>
                  )}
                  <div className="flex justify-between items-center p-3 sm:p-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 
                          ? 'bg-gradient-to-r from-purple-400 to-indigo-500 text-white shadow-lg' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {week.rank}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-xs sm:text-sm">{week.week}</div>
                        <div className={`text-xs font-medium ${
                          index === 0 ? 'text-indigo-600' : 'text-gray-500'
                        }`}>
                          {index === 0 ? 'League Record' : 'Avg MoV'}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm sm:text-xl font-bold ${
                      index === 0 ? 'text-indigo-600' : 'text-purple-600'
                    }`}>
                      {week.avgMoV}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Week Details - Mobile optimized */}
      {selectedWeek && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl max-w-sm sm:max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden border border-white/30">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 sm:p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20"></div>
              <div className="relative flex items-center justify-between">
                <div>
                  <h2 className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2 tracking-tight">
                    {selectedWeek.year} Week {selectedWeek.week}
                  </h2>
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    <p className="text-blue-100 text-sm sm:text-lg font-medium">
                      {weekMatchups.length} matchups • League showdown
                    </p>
                    {selectedWeek.category && (
                      <div className="flex items-center space-x-2">
                        <div className="bg-white/20 px-2 sm:px-3 py-1 rounded-full">
                          <span className="text-xs sm:text-sm font-bold">{selectedWeek.category} Record</span>
                        </div>
                        <div className="bg-white/20 px-2 sm:px-3 py-1 rounded-full">
                          <span className="text-xs sm:text-sm font-bold">#{selectedWeek.rank}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 sm:p-3 hover:bg-white/20 rounded-xl sm:rounded-2xl transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  <X className="h-5 w-5 sm:h-7 sm:w-7" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-8 overflow-y-auto max-h-[70vh] bg-gradient-to-br from-gray-50/50 to-white/50">
              <div className="space-y-4 sm:space-y-6">
                {weekMatchups.map((matchup, index) => (
                  <div 
                    key={index}
                    className="relative bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-8 border border-gray-200/50 hover:shadow-2xl hover:border-purple-200/50 transition-all duration-300 hover:scale-[1.01] group"
                  >
                    {/* Premium gradient border for winner */}
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 via-blue-400/20 to-purple-400/20 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Manager 1 */}
                      <div className={`text-center flex-1 transition-all duration-300 ${
                        matchup.winner === matchup.manager1 ? 'opacity-100 scale-105' : 'opacity-75'
                      }`}>
                        <div className={`text-lg sm:text-xl font-bold mb-2 ${
                          matchup.winner === matchup.manager1 ? 'text-emerald-600' : 'text-gray-700'
                        }`}>
                          {matchup.manager1}
                        </div>
                        <div className={`text-2xl sm:text-4xl font-black tracking-tight ${
                          matchup.winner === matchup.manager1 
                            ? 'text-emerald-700 drop-shadow-sm' 
                            : 'text-gray-600'
                        }`}>
                          {matchup.manager1Score.toFixed(2)}
                        </div>
                        {matchup.winner === matchup.manager1 && (
                          <div className="flex items-center justify-center mt-2 sm:mt-3">
                            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-1.5 sm:p-2 rounded-full shadow-lg">
                              <Trophy className="h-3 w-3 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <span className="text-xs sm:text-sm font-bold text-emerald-600 ml-2 tracking-wide">WINNER</span>
                          </div>
                        )}
                      </div>

                      {/* VS Divider - Enhanced */}
                      <div className="flex flex-col items-center mx-4 sm:mx-8 relative">
                        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-2 sm:p-4 rounded-xl sm:rounded-2xl shadow-xl mb-1 sm:mb-2">
                          <Sword className="h-4 w-4 sm:h-8 sm:w-8 text-white" />
                        </div>
                        <span className="text-sm sm:text-lg font-bold text-gray-600 tracking-wider">VS</span>
                        <div className="mt-2 sm:mt-3 bg-gradient-to-r from-orange-100 to-red-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-orange-200">
                          <span className="text-xs sm:text-sm font-bold text-orange-700">
                            +{matchup.margin.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Manager 2 */}
                      <div className={`text-center flex-1 transition-all duration-300 ${
                        matchup.winner === matchup.manager2 ? 'opacity-100 scale-105' : 'opacity-75'
                      }`}>
                        <div className={`text-lg sm:text-xl font-bold mb-2 ${
                          matchup.winner === matchup.manager2 ? 'text-emerald-600' : 'text-gray-700'
                        }`}>
                          {matchup.manager2}
                        </div>
                        <div className={`text-2xl sm:text-4xl font-black tracking-tight ${
                          matchup.winner === matchup.manager2 
                            ? 'text-emerald-700 drop-shadow-sm' 
                            : 'text-gray-600'
                        }`}>
                          {matchup.manager2Score.toFixed(2)}
                        </div>
                        {matchup.winner === matchup.manager2 && (
                          <div className="flex items-center justify-center mt-2 sm:mt-3">
                            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-1.5 sm:p-2 rounded-full shadow-lg">
                              <Trophy className="h-3 w-3 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <span className="text-xs sm:text-sm font-bold text-emerald-600 ml-2 tracking-wide">WINNER</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Enhanced margin indicator */}
                    <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gradient-to-r from-transparent via-gray-200 to-transparent">
                      <div className="text-center">
                        <div className="inline-flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl border border-blue-200/50">
                          <Target className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 mr-2" />
                          <span className="text-xs sm:text-sm font-semibold text-blue-700">
                            Margin of Victory: <span className="text-blue-800 font-bold">{matchup.margin.toFixed(2)} points</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Enhanced Week Statistics - Mobile responsive */}
              <div className="mt-6 sm:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl sm:rounded-3xl p-3 sm:p-6 text-white shadow-2xl border border-blue-400/30">
                  <div className="absolute top-0 right-0 w-16 h-16 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-8 sm:-translate-y-16 translate-x-8 sm:translate-x-16"></div>
                  <div className="relative">
                    <BarChart3 className="h-4 w-4 sm:h-8 sm:w-8 mb-2 sm:mb-3 text-blue-200" />
                    <div className="text-lg sm:text-3xl font-black tracking-tight mb-1">
                      {weekMatchups.reduce((sum, m) => sum + m.manager1Score + m.manager2Score, 0).toFixed(2)}
                    </div>
                    <div className="text-blue-200 font-semibold tracking-wide text-xs sm:text-sm">TOTAL POINTS</div>
                  </div>
                </div>
                
                <div className="relative overflow-hidden bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl sm:rounded-3xl p-3 sm:p-6 text-white shadow-2xl border border-orange-400/30">
                  <div className="absolute top-0 right-0 w-16 h-16 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-8 sm:-translate-y-16 translate-x-8 sm:translate-x-16"></div>
                  <div className="relative">
                    <Target className="h-4 w-4 sm:h-8 sm:w-8 mb-2 sm:mb-3 text-orange-200" />
                    <div className="text-lg sm:text-3xl font-black tracking-tight mb-1">
                      {(weekMatchups.reduce((sum, m) => sum + m.margin, 0) / weekMatchups.length).toFixed(2)}
                    </div>
                    <div className="text-orange-200 font-semibold tracking-wide text-xs sm:text-sm">AVG MARGIN</div>
                  </div>
                </div>
                
                <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl sm:rounded-3xl p-3 sm:p-6 text-white shadow-2xl border border-emerald-400/30">
                  <div className="absolute top-0 right-0 w-16 h-16 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-8 sm:-translate-y-16 translate-x-8 sm:translate-x-16"></div>
                  <div className="relative">
                    <Trophy className="h-4 w-4 sm:h-8 sm:w-8 mb-2 sm:mb-3 text-emerald-200" />
                    <div className="text-lg sm:text-3xl font-black tracking-tight mb-1">
                      {Math.max(...weekMatchups.map(m => Math.max(m.manager1Score, m.manager2Score))).toFixed(2)}
                    </div>
                    <div className="text-emerald-200 font-semibold tracking-wide text-xs sm:text-sm">TOP SCORE</div>
                  </div>
                </div>
                
                <div className="relative overflow-hidden bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl sm:rounded-3xl p-3 sm:p-6 text-white shadow-2xl border border-purple-400/30">
                  <div className="absolute top-0 right-0 w-16 h-16 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-8 sm:-translate-y-16 translate-x-8 sm:translate-x-16"></div>
                  <div className="relative">
                    <Star className="h-4 w-4 sm:h-8 sm:w-8 mb-2 sm:mb-3 text-purple-200" />
                    <div className="text-lg sm:text-3xl font-black tracking-tight mb-1">
                      {Math.max(...weekMatchups.map(m => m.manager1Score + m.manager2Score)).toFixed(2)}
                    </div>
                    <div className="text-purple-200 font-semibold tracking-wide text-xs sm:text-sm">TOP MATCHUP</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaguePerformance;